import styled from "@emotion/styled";
import { sizes } from "../../../constants/spacing";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
import { RequiredThemeKeys } from "../../../util/theme";
import { withFocusToggleClassName } from "../../../util/withFocusClassName";
import { menuTheme } from "./menuTheme";

export interface MenuItemStyledBaseProps {
    isDisabled: boolean;
    layout: "compact" | "normal";
    themeMode: RequiredThemeKeys;
}

/**
 * Handles the most basic styling of a menu item:
 *  - how it sits in its container
 *  - colors
 *  - padding/margins
 *  - focus states
 *  - hover states
 *  - disabled states
 *  - etc.
 *
 * To provide maximum flexibility, this component should NOT add styles around
 * how its contents are laid out or the spacing between any of them.
 *
 */
export const MenuItemBaseStyled = withFocusToggleClassName(styled("button")<MenuItemStyledBaseProps>`
    background-color: ${({ themeMode }) => menuTheme.backgroundColor.rest(themeMode)};
    // min-height to allow growing for metadata items, or other customizations.
    min-height: ${({ layout, themeMode }) => menuTheme.optionHeight[layout](themeMode)}px;
    border: 0;
    margin: 0 ${sizes.xSmall}px;
    padding: 0 ${sizes.xSmall}px;
    text-align: left;
    font-weight: 500;
    color: ${({ themeMode }) => menuTheme.fontColor.rest(themeMode)};
    border-radius: ${({ themeMode }) => menuTheme.borderRadius(themeMode)};
    transition: ${({ themeMode }) => menuTheme.motion.itemBackground(themeMode)};

    &:focus,
    &[aria-expanded="true"] {
        background-color: ${({ themeMode }) => menuTheme.backgroundColor.focus(themeMode)};
        color: ${({ themeMode }) => menuTheme.fontColor.focus(themeMode)};
    }

    &:active {
        background-color: ${({ themeMode }) => menuTheme.backgroundColor.pressed(themeMode)};
        color: ${({ themeMode }) => menuTheme.fontColor.focus(themeMode)};
    }

    ${({ isDisabled, themeMode }) =>
        isDisabled &&
        `
        color: ${menuTheme.fontColor.disabled(themeMode)};

        &:hover {
            background-color: transparent;
            transition: none;
        }

        &:focus {
            font-weight: 500;
            color: ${menuTheme.fontColor.disabled(themeMode)};
        }
    `}

    // assists in rendering reasonably laid-out children.
    display: flex;
    flex-direction: row;
    align-items: center;

    // Reset ua stylesheet.
    font-family: inherit;
    font-size: ${({ themeMode }) => menuTheme.fontSize(themeMode)};
    cursor: pointer;

    // For the highlight ::after position: absolute.
    position: relative;

    ${({ themeMode }) =>
        createFocusRingStyles({
            gap: 0,
            componentRadius: 4,
            theme: themeMode,
            componentBorderWidth: 0,
        })}

    ::before {
        z-index: 2;
    }

    & > * {
        z-index: 2;
        // To allow the z-index to compare against the absolute ::before.
        position: relative;
    }
`);
MenuItemBaseStyled.displayName = "MenuItemBaseStyled";
